import Phaser from 'phaser';
import { SettingsState } from '../state/SettingsState';

// Audio system for SFX and music
const MENU_MUSIC_KEY = 'menu-music-1';
const MENU_MUSIC_VOLUME = 0.5; // 50% volume
const MENU_MUSIC_FADE_IN_MS = 1000;
const MENU_MUSIC_FADE_OUT_MS = 600;

// BPM info for each music track (for dance sync; menu BPM from filenames)
const TRACK_BPM: Record<string, number> = {
  'bgm-1': 56,
  'bgm-2': 84,
  'bgm-3': 56,
  'bgm-4': 95,
  'bgm-5': 95,
  'bgm-6': 88,
  'bgm-7': 94,
  'menu-music-1': 88,
  'menu-music-2': 90,
};

class AudioRouterManager {
  private scene: Phaser.Scene | null = null;
  private currentMusic: Phaser.Sound.BaseSound | null = null;

  // Menu music (persists across menu/map/settings; stopped when entering gameplay)
  private menuMusic: Phaser.Sound.WebAudioSound | null = null;

  // Current BPM for dance sync
  private currentBPM: number = 100;
  private currentTrackKey: string = '';

  // Throttling for coin collection sound (max once per 100ms)
  private lastCoinSoundTime: number = 0;
  private readonly COIN_SOUND_THROTTLE_MS: number = 100;

  // Active construction sounds (keyed by tower ID)
  private constructionSounds: Map<string, Phaser.Sound.BaseSound> = new Map();

  init(scene: Phaser.Scene): void {
    this.scene = scene;
    this.lastCoinSoundTime = 0;
    this.constructionSounds.clear();
    console.log('[AudioRouter] Initialized with scene');
  }

  /**
   * Play a sound effect with optional volume multiplier
   * @param key - The audio key to play
   * @param volumeMultiplier - Optional multiplier for this specific sound (0.0 to 1.0)
   */
  playSfx(key: string, volumeMultiplier: number = 1.0): void {
    if (!this.scene) {
      console.warn(`[AudioRouter] Cannot play SFX '${key}' - no scene`);
      return;
    }

    const baseVolume = SettingsState.getSfxVolume();
    const finalVolume = baseVolume * volumeMultiplier;
    
    if (finalVolume <= 0) {
      console.log(`[AudioRouter] SFX '${key}' skipped - volume is 0`);
      return;
    }

    // Check if the audio key exists
    if (!this.scene.sound.get(key) && !this.scene.cache.audio.exists(key)) {
      console.warn(`[AudioRouter] SFX '${key}' not found in cache`);
      return;
    }

    try {
      this.scene.sound.play(key, { volume: finalVolume });
      console.log(`[AudioRouter] Playing SFX: ${key} at volume ${finalVolume.toFixed(2)} (base: ${baseVolume.toFixed(2)}, mult: ${volumeMultiplier})`);
    } catch (error) {
      console.error(`[AudioRouter] Error playing SFX '${key}':`, error);
    }
  }

  // Play background music
  playMusic(key: string, loop: boolean = true): void {
    if (!this.scene) return;

    this.stopMusic();

    // Update BPM for dance sync
    this.currentTrackKey = key;
    this.currentBPM = TRACK_BPM[key] ?? 100;
    console.log(`[AudioRouter] BPM set to ${this.currentBPM} for track ${key}`);

    const volume = SettingsState.getMusicVolume();
    console.log(`[Audio] Music: ${key} at volume ${volume}, loop: ${loop}`);
  }

  // Stop current music
  stopMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic = null;
    }
  }

  /**
   * Set the current BPM (called when music track changes in GameplayScene)
   */
  setCurrentBPM(bpm: number, trackKey?: string): void {
    this.currentBPM = bpm;
    if (trackKey) this.currentTrackKey = trackKey;
    console.log(`[AudioRouter] BPM set to ${bpm}${trackKey ? ` for ${trackKey}` : ''}`);
  }

  /**
   * Get the current BPM for dance sync. Tracks below 80 BPM are doubled so slow songs dance twice as fast.
   */
  getCurrentBPM(): number {
    const bpm = this.currentBPM;
    return bpm < 80 ? bpm * 2 : bpm;
  }

  /**
   * Get the current track key (for logging which beat we're dancing to)
   */
  getCurrentTrackKey(): string {
    return this.currentTrackKey;
  }

  /**
   * Get BPM for a specific track key
   */
  getBPMForTrack(trackKey: string): number {
    return TRACK_BPM[trackKey] ?? 100;
  }

  /**
   * Start menu music with fade-in at 50% volume. Call from MenuScene, MapScene, SettingsScene.
   */
  startMenuMusic(scene: Phaser.Scene): void {
    if (!scene.cache.audio.exists(MENU_MUSIC_KEY)) return;

    const targetVolume = MENU_MUSIC_VOLUME * SettingsState.getMusicVolume();
    if (targetVolume <= 0) return;

    // Already playing — leave as is (or could re-fade to 50% if settings changed)
    if (this.menuMusic && this.menuMusic.isPlaying) {
      return;
    }

    // Create or reuse menu music
    if (!this.menuMusic) {
      try {
        this.menuMusic = scene.sound.add(MENU_MUSIC_KEY, {
          volume: 0,
          loop: true,
        }) as Phaser.Sound.WebAudioSound;
      } catch (e) {
        console.warn('[AudioRouter] Could not add menu music:', e);
        return;
      }
    }

    this.menuMusic.setVolume(0);
    this.menuMusic.play();

    // Set BPM for menu/map dance sync (e.g. map selector dancer)
    this.currentTrackKey = MENU_MUSIC_KEY;
    this.currentBPM = TRACK_BPM[MENU_MUSIC_KEY] ?? 100;

    // Fade in
    const obj = { v: 0 };
    scene.tweens.add({
      targets: obj,
      v: targetVolume,
      duration: MENU_MUSIC_FADE_IN_MS,
      ease: 'Linear',
      onUpdate: () => {
        if (this.menuMusic) this.menuMusic.setVolume(obj.v);
      },
    });
  }

  /**
   * Fade out and stop menu music. Call when entering GameplayScene (game level).
   */
  stopMenuMusic(scene: Phaser.Scene): void {
    if (!this.menuMusic) return;

    const obj = { v: (this.menuMusic as Phaser.Sound.WebAudioSound).volume };
    scene.tweens.add({
      targets: obj,
      v: 0,
      duration: MENU_MUSIC_FADE_OUT_MS,
      ease: 'Linear',
      onUpdate: () => {
        if (this.menuMusic) (this.menuMusic as Phaser.Sound.WebAudioSound).setVolume(obj.v);
      },
      onComplete: () => {
        if (this.menuMusic) {
          this.menuMusic.stop();
          // Keep reference so we can play again when returning to menu
        }
      },
    });
  }

  // Update volumes (call when settings change)
  updateVolumes(): void {
    console.log('[Audio] Volumes updated');
  }

  // UI sound helpers
  playUIClick(): void {
    this.playSfx('ui_click');
  }

  playUIHover(): void {
    this.playSfx('ui_hover');
  }

  playUIBack(): void {
    this.playSfx('ui_back');
  }

  // Game sound helpers
  playTowerBuild(): void {
    this.playSfx('tower_build');
  }

  /**
   * Play monster death sound at 30% volume
   */
  playMonsterDeath(): void {
    this.playSfx('sfx-dying', 0.3);
  }

  /**
   * Play tower fire sound based on tower type.
   * @param options.playTwice - if true, play the same sound again after a short delay (e.g. antiair at black)
   */
  playTowerFire(towerType: string, options?: { playTwice?: boolean }): void {
    // Map tower types to their SFX keys
    const sfxMap: Record<string, string> = {
      'arrow': 'sfx-arrow',
      'cannon': 'sfx-cannon',
      'antiair': 'sfx-antiair-2',
      'tesla': 'sfx-tesla',
      'fire': 'sfx-fire',
      'ice': 'sfx-ice',
      'laser': 'sfx-laser',
      'mortar': 'sfx-mortar',
    };

    const sfxKey = sfxMap[towerType];
    if (sfxKey) {
      this.playSfx(sfxKey);
      if (options?.playTwice && this.scene) {
        this.scene.time.delayedCall(70, () => this.playSfx(sfxKey));
      }
    } else {
      // Fall back to arrow sound for towers without specific SFX
      console.log(`[AudioRouter] No specific SFX for tower type '${towerType}', using fallback`);
      this.playSfx('sfx-arrow', 0.5);
    }
  }

  playWaveStart(): void {
    this.playSfx('wave_start');
  }

  playVictory(): void {
    this.playSfx('victory');
  }

  playDefeat(): void {
    this.playSfx('defeat');
  }

  /**
   * Play coin collection sound (throttled to max once per 0.5s)
   */
  playCoinCollect(): void {
    const now = Date.now();
    if (now - this.lastCoinSoundTime < this.COIN_SOUND_THROTTLE_MS) {
      // Throttled - don't play
      return;
    }
    
    this.lastCoinSoundTime = now;
    this.playSfx('sfx-collecting');
  }

  /**
   * Start looping construction sound for a tower
   * @param towerId - Unique ID to track which tower this is for
   */
  startConstruction(towerId: string): void {
    if (!this.scene) {
      console.warn(`[AudioRouter] Cannot start construction sound - no scene`);
      return;
    }

    // Don't start if already playing for this tower
    if (this.constructionSounds.has(towerId)) {
      return;
    }

    const baseVolume = SettingsState.getSfxVolume();
    if (baseVolume <= 0) return;

    // Check if the audio key exists
    if (!this.scene.cache.audio.exists('sfx-construction')) {
      console.warn(`[AudioRouter] SFX 'sfx-construction' not found in cache`);
      return;
    }

    try {
      const sound = this.scene.sound.add('sfx-construction', {
        volume: baseVolume * 0.5, // Construction at 50% volume
        loop: true,
      });
      sound.play();
      this.constructionSounds.set(towerId, sound);
      console.log(`[AudioRouter] Started construction sound for tower ${towerId}`);
    } catch (error) {
      console.error(`[AudioRouter] Error starting construction sound:`, error);
    }
  }

  /**
   * Stop construction sound for a tower
   * @param towerId - The tower ID to stop construction sound for
   */
  stopConstruction(towerId: string): void {
    const sound = this.constructionSounds.get(towerId);
    if (sound) {
      sound.stop();
      sound.destroy();
      this.constructionSounds.delete(towerId);
      console.log(`[AudioRouter] Stopped construction sound for tower ${towerId}`);
    }
  }

  /**
   * Stop all construction sounds (for cleanup)
   */
  stopAllConstruction(): void {
    this.constructionSounds.forEach((sound, towerId) => {
      sound.stop();
      sound.destroy();
      console.log(`[AudioRouter] Stopped construction sound for tower ${towerId}`);
    });
    this.constructionSounds.clear();
  }

  destroy(): void {
    this.stopMusic();
    if (this.menuMusic) {
      this.menuMusic.stop();
      this.menuMusic = null;
    }
    this.stopAllConstruction();
    this.scene = null;
  }
}

// Singleton instance
export const AudioRouter = new AudioRouterManager();
